/* @flow weak */

import { intersection } from "lodash/array"
import { toNumber } from "lodash/lang"
import ChangePassword from "./components/pages/ChangePassword"
import CMSPage from "./components/pages/CMSPage"
import Collection from "./components/pages/Collection"
import Collections from "./components/pages/Collections"
import FeaturedCollections from "./components/pages/FeaturedCollections"
import ConfirmEmail from "./components/pages/ConfirmEmail"
import Contribute from "./components/pages/Contribute"
import EditMediaForm from "./components/pages/EditMediaForm"
import Home from "./components/pages/Home"
import Item from "./components/pages/Item"
import Layout from "./components/Layout"
import Login from "./components/pages/Login"
import LoginButton from "./components/elements/LoginButton"
import RegisterButton from "./components/elements/RegisterButton"
import LoginLayout from "./components/LoginLayout"
import MediaForm from "./components/pages/MediaForm"
import PasswordReset from "./components/pages/PasswordReset"
import PDFiframe from "./components/pages/PDFiframe"
import ProfileForm from "./components/modules/ProfileForm"
import PublicSet from "./components/pages/PublicSet"
import PublicSets from "./components/pages/PublicSets"
import Register from "./components/pages/Register"
import Search from "./components/pages/Search"
import Sets from "./components/modules/Sets"
import UnlockAccount from "./components/pages/UnlockAccount"
import User from "./components/pages/User"
import UserContributions from "./components/modules/UserContributions"
import UserSet from "./components/modules/UserSet"
import WriteArticle from "./components/pages/WriteArticle"
import {
  ViewerQuery,
  ResourceQuery,
  UserSetQuery,
  Query,
  ResourceItemQuery
} from "./queries"
import { dispatch } from "./Dispatcher"
import State from "./stores/State"
import { filterableResourceTypeNames } from "./scripts/resourceTypes"
import { filterHTML } from "./scripts/xss"

function leavePage() {
  dispatch({ type: "leave-page" })
}

function enterPage() {
  if (typeof window === "undefined") return
  window.scrollTo(0, 0)
}

function requireAuth(nextState, replace) {
  if (!State.get("loggedIn")) {
    replace(`/login?next=${nextState.location.pathname}`)
    enterPage()
  }
}

function forceArray(value) {
  if (typeof value === "string") {
    return [value]
  }
  return value
}

const SEARCH_QUERY_PARAMS = [
  "q",
  "terms",
  "types",
  "tags",
  "sort",
  "total",
  "perPage",
  "map",
  "bbox"
]

function prepareSearchParams(params) {
  // These param values will be available to the Relay query

  // Force these to always be arrays
  const forceToArray = ["terms", "tags", "types"]
  forceToArray.forEach(field => {
    if (params[field]) {
      params[field] = forceArray(params[field])
    }
  })

  for (const field in params) {
    params[field] = filterHTML(params[field])
  }

  const preparedParams = {
    q: params.q || "",
    map: !!params.map,
    sort: params.sort || null,
    terms: params.terms || [],
    tags: params.tags || [],
    types: params.types
      ? intersection(params.types, filterableResourceTypeNames())
      : [],
    filters: params.filters || {},
    breadcrumbs: params.breadcrumbs || {},
    dates: params.dates || {},
    total: (() => {
      const total = toNumber(params.total)
      if (total && total < 1000) return total
    })(),
    perPage: (() => {
      const perPage = toNumber(params.perPage)
      if (perPage && perPage <= 90) return perPage
    })()
  }

  return preparedParams
}

function prepareCollectionParams(params) {
  const preparedParams = {
    ...params,
    total: (() => {
      const total = toNumber(params.total)
      if (total && total < 1000) return total
    })()
  }

  return preparedParams
}

export const encodeBreadcrumbsParameters = (name, values) => {
  const searchHash = JSON.stringify({ [name]: values })

  return encodeURIComponent(Buffer.from(searchHash, "utf8").toString("base64"))
}

export const decodeBreadcrumbsParameters = encodedString => {
  try {
    const data = decodeURIComponent(
      Buffer.from(encodedString, "base64").toString("utf8")
    )
    return JSON.parse(data)
  } catch (error) {
    console.error("encodedString", encodedString)
    return {}
  }
}

export default [
  {
    path: "/login",
    component: LoginLayout,
    indexRoute: {
      components: { main: Login, right: RegisterButton },
      queries: { main: ViewerQuery },
      onLeave: leavePage,
      onEnter: enterPage
    }
  },
  {
    path: "/confirm-email",
    component: LoginLayout,
    indexRoute: {
      components: { main: ConfirmEmail },
      onLeave: leavePage,
      onEnter: enterPage
    }
  },
  {
    path: "/change-password",
    component: LoginLayout,
    indexRoute: {
      components: { main: ChangePassword },
      onLeave: leavePage,
      onEnter: enterPage
    }
  },
  {
    path: "/unlock-account",
    component: LoginLayout,
    indexRoute: {
      components: { main: UnlockAccount },
      onLeave: leavePage,
      onEnter: enterPage
    }
  },
  {
    path: "/register",
    component: LoginLayout,
    indexRoute: {
      components: { main: Register, right: LoginButton },
      queries: { main: ViewerQuery },
      onLeave: leavePage,
      onEnter: enterPage
    }
  },
  {
    path: "/reset-password",
    component: LoginLayout,
    indexRoute: {
      component: { main: PasswordReset },
      onLeave: leavePage,
      onEnter: enterPage
    }
  },
  {
    path: "/",
    component: Layout,
    queries: ViewerQuery,
    indexRoute: {
      component: Home,
      queries: ViewerQuery,
      onLeave: leavePage,
      onEnter: enterPage
      //onEnter: (nextState, replace) => replace('/preview')
    },
    action: () => window.scrollTo(0, 0),
    childRoutes: [
      {
        path: "search",
        indexRoute: {
          component: Search,
          queries: ViewerQuery,
          queryParams: SEARCH_QUERY_PARAMS,
          prepareParams: prepareSearchParams,
          onLeave: leavePage,
          onEnter: enterPage
        }
        // childRoutes: [
        //   {
        //     path: "map",
        //     indexRoute: {
        //       component: Search,
        //       queries: ViewerQuery,
        //       queryParams: SEARCH_QUERY_PARAMS,
        //       prepareParams: prepareSearchParams,
        //       onLeave: leavePage
        //     }
        //   }
        // ]
      },
      {
        path: "item/:uuid",
        indexRoute: {
          component: Item,
          queries: {
            ...ResourceQuery,
            ...ViewerQuery
          },
          onLeave: leavePage,
          onEnter: enterPage,
          queryParams: ["child", "file"]
        },
        childRoutes: [
          {
            path: "pdf",
            component: PDFiframe,
            queries: ResourceQuery,
            onLeave: leavePage,
            onEnter: enterPage,
            queryParams: ["file"]
          }
        ]
      },
      {
        path: "upload-media",
        indexRoute: {
          component: MediaForm,
          onLeave: leavePage,
          onEnter: requireAuth
        }
      },
      {
        path: "edit-media/:uuid",
        indexRoute: {
          component: EditMediaForm,
          queries: ResourceQuery,
          onLeave: leavePage,
          onEnter: requireAuth
        }
      },
      {
        path: "write-article",
        indexRoute: {
          component: WriteArticle,
          onLeave: leavePage,
          onEnter: requireAuth
        }
      },
      {
        path: "contribute",
        indexRoute: {
          component: Contribute,
          onLeave: leavePage,
          onEnter: enterPage
        }
      },
      {
        path: "user",
        component: User,
        onEnter: requireAuth,
        onLeave: leavePage,
        queries: ViewerQuery,
        indexRoute: {
          onEnter: (nextState, replace) => replace("/user/sets")
        },
        childRoutes: [
          {
            path: "sets",
            indexRoute: {
              component: Sets,
              queries: ViewerQuery
            },
            childRoutes: [
              {
                path: ":id",
                component: UserSet,
                queries: {
                  ...ViewerQuery,
                  ...UserSetQuery
                }
              }
            ]
          },
          {
            path: "contributions",
            component: UserContributions,
            queries: ViewerQuery
          },
          {
            path: "profile",
            component: ProfileForm
          }
        ]
      },
      {
        path: "collections",
        indexRoute: {
          component: Collections,
          queries: ViewerQuery,
          queryParams: ["q"],
          onLeave: leavePage,
          onEnter: enterPage
        },
        childRoutes: [
          {
            path: ":slug",
            indexRoute: {
              component: Collection,
              queries: ViewerQuery,
              queryParams: ["total"],
              prepareParams: prepareCollectionParams,
              onLeave: leavePage,
              onEnter: enterPage
            },
            childRoutes: [
              {
                path: ":itemUuid",
                indexRoute: {
                  component: Item,
                  queries: {
                    ...ResourceItemQuery,
                    ...ViewerQuery
                  },
                  onLeave: leavePage,
                  onEnter: enterPage
                }
              }
            ]
          }
        ]
      },
      {
        path: "featured-collections",
        indexRoute: {
          component: FeaturedCollections,
          queries: ViewerQuery,
          onLeave: leavePage,
          onEnter: enterPage
        },
        childRoutes: [
          {
            path: ":slug",
            indexRoute: {
              component: Collection,
              queries: {
                ...ViewerQuery,
                ...ResourceQuery
              },
              queryParams: ["total"],
              prepareParams: prepareCollectionParams,
              onLeave: leavePage,
              onEnter: enterPage
            }
          }
        ]
      },
      {
        path: "sets",
        indexRoute: {
          component: PublicSets,
          queries: Query,
          onLeave: leavePage,
          onEnter: enterPage
        },
        childRoutes: [
          {
            path: ":id",
            queries: UserSetQuery,
            component: PublicSet
          }
        ]
      },
      {
        path: ":slug",
        indexRoute: {
          component: CMSPage,
          queries: ViewerQuery,
          onLeave: leavePage,
          onEnter: enterPage
        }
      }
    ]
  }
]



// WEBPACK FOOTER //
// ./webapp/routes.js